import axios from 'axios';
import { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import './ProfessionalForm.css';

// const BASE_URL = "http://localhost:8000";
const BASE_URL = "https://idealpursuits.co.in/api";

function ProfessionalForm() {
    const [submitted, setSubmitted] = useState(false);
    const navigate = useNavigate();
    const [repass, setRepass] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        meetingCharge: '',
        address: '',
        profession: '',
        category: '',
        experience: '',
        qualifications: '',
        resumeDoc: null,
        proofDoc: null,
        profileImage: null,
        resumeDocName: null,
        proofDocName: null,
        profileImageName: null,
        isApproved: false,
    });

    const [error, setError] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleResumeFileChange = (e) => {
        const { files } = e.target;

        const allowedExtensions = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'application/msword'];

        if (files[0] && !allowedExtensions.includes(files[0].type)) {
            alert('Invalid file type. Please upload a PNG, JPEG, JPG, PDF, or DOC file.');
            e.target.value = '';
            return;
        }

        // Generate a smaller UUID
        const smallUUID = uuidv4().slice(0, 8); // Adjust length as needed

        setFormData((prev) => ({
            ...prev,
            ['resumeDoc']: files[0],
            [`resumeDocName`]: `${smallUUID}_${files[0]?.name}`, // Dynamically set doc name based on input name
        }));
    };

    const handleProofFileChange = (e) => {
        const { files } = e.target;

        const allowedExtensions = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'application/msword'];

        if (files[0] && !allowedExtensions.includes(files[0].type)) {
            alert('Invalid file type. Please upload a PNG, JPEG, JPG, PDF, or DOC file.');
            e.target.value = '';
            return;
        }

        // Generate a smaller UUID
        const smallUUID = uuidv4().slice(0, 8); // Adjust length as needed

        setFormData((prev) => ({
            ...prev,
            ['proofDoc']: files[0],
            [`proofDocName`]: `${smallUUID}_${files[0]?.name}`, // Dynamically set doc name based on input name
        }));
    };

    const handleProfileFileChange = (e) => {
        const { files } = e.target;

        const allowedExtensions = ['image/png', 'image/jpeg', 'image/jpg'];

        if (files[0] && !allowedExtensions.includes(files[0].type)) {
            alert('Invalid file type. Please upload a PNG, JPEG, or JPG file.');
            e.target.value = '';
            return;
        }

        // Generate a smaller UUID
        const smallUUID = uuidv4().slice(0, 8); // Adjust length as needed

        setFormData((prev) => ({
            ...prev,
            ['profileImage']: files[0],
            [`profileImageName`]: `${smallUUID}_${files[0]?.name}`, // Dynamically set doc name based on input name
        }));
    };

    const handleClose = () => {
        navigate('/');  // Navigate to the home page when the close button is clicked
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate file uploads
        if (!formData.profileImage || !formData.resumeDoc || !formData.proofDoc) {
            setError('All three files are required.');
            return;
        }

        if (formData.password !== repass){
            setError('The passwords are not matching.');
            return;
        }

        const submissionData = new FormData();
        Object.keys(formData).forEach((key) => {
            submissionData.append(key, formData[key]);
        });

        onSubmit(submissionData);
    };

    const onSubmit = async (formData) => {
        try {
            const formDataToSend = new FormData();
            Object.keys(formData).forEach(key => {
                formDataToSend.append(key, formData[key]);
            });

            const response = await axios.post(`${BASE_URL}/expert/addExpertDetails.php`, formData);

            const result = response.data;
            if (result.success) {
                setSubmitted(true);
            } else {
                alert(result.error || 'Failed to add your data. Please try again.');
            }
        } catch (error) {
            console.error('Error adding expert:', error);
            if (error.message && error.response.data.message) {
                alert(`Error: ${error.response.data.message}`);
            } else {
                alert('Failed to add expert. Please try again.');
            }
        }
    };

    const handleRemoveFile = (inputName) => {
        const input = document.querySelector(`input[name="${inputName}"]`);
        if (input) {
            input.value = "";  // Clears the selected file from the input field
            setFormData((prev) => ({
                ...prev,
                [inputName]: null,
                [`${inputName}Name`]: null,
            }));
        }
    };

    if (submitted) {
        return (
            <div className="career-coach-form-overlay">
                <div className="career-coach-form-submitted">
                    <span className="close" onClick={handleClose}>&times;</span>
                    <h2>Your form has been submitted</h2>
                    <p>You'll receive an email in a day or two.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="career-coach-form-overlay">
            <div className="career-coach-form">
                <span className="close" onClick={handleClose}>&times;</span>
                <h2>Professional Enrollment</h2>
                {error && <div className="error-message">{error}</div>}
                <form onSubmit={handleSubmit}>
                    {/* Input Fields */}
                    <div className="form-group">
                        <label>Name:</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Email:</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Phone:</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Password</label>
                        <div className="email-verify">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter your password"
                                required
                            />
                            <button
                                type="button"
                                className="show-password-btn"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Re-enter Password</label>
                        <div className="email-verify">
                            <input
                                type="password"
                                id="password"
                                value={repass}
                                onChange={(e) => setRepass(e.target.value)}
                                placeholder="Re-enter your password"
                                required
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Address:</label>
                        <textarea
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Profession:</label>
                        <input
                            type="text"
                            name="profession"
                            value={formData.profession}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Category:</label>
                        <input
                            type="text"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Experience:</label>
                        <input
                            type="text"
                            name="experience"
                            value={formData.experience}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Qualifications:</label>
                        <input
                            type="text"
                            name="qualifications"
                            value={formData.qualifications}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    {/* File Uploads */}
                    <div className="form-group">
                        <label>Resume Document (Required)(img/pdf):</label>
                        <input
                            type="file"
                            name="resumeDoc"
                            accept=".jpg,.jpeg,.png,.doc,.docx,.pdf"
                            onChange={handleResumeFileChange}
                            required
                        />
                        {formData.resumeDoc && (
                            <button
                                type="button"
                                onClick={() => handleRemoveFile('resumeDoc')}
                                className="remove-file-btn"
                            >
                                Remove file
                            </button>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Proof Document (Required)(img/pdf):</label>
                        <input
                            type="file"
                            name="proofDoc"
                            accept=".jpg,.jpeg,.png,.doc,.docx,.pdf"
                            onChange={handleProofFileChange}
                            required
                        />
                        {formData.proofDoc && (
                            <button
                                type="button"
                                onClick={() => handleRemoveFile('proofDoc')}
                                className="remove-file-btn"
                            >
                                Remove file
                            </button>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Profile Image (Required)(img):</label>
                        <input
                            type="file"
                            name="profileImage"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleProfileFileChange}
                            required
                        />
                        {formData.profileImage && (
                            <button
                                type="button"
                                onClick={() => handleRemoveFile('profileImage')}
                                className="remove-file-btn"
                            >
                                Remove file
                            </button>
                        )}
                    </div>
                    <button type="submit" className='enroll-button'>Enroll as a Professional</button>
                </form>
            </div>
        </div>
    );
}

export default ProfessionalForm;
